import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { GATE_CATALOG_ITEM_TYPES } from "../../../enums/gateCatalogItemTypes.enum";
import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { ImportResponse } from "../../../models/api/importResponse";
import { catchError, throwError } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ImportService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);

  /**
   * COMPONENTS IMPORT FUNCTIONS
   */

  /**
   * Create an import for gate catalog items.
   * @param file The csv file to be imported.
   * @param importType The type of the gate catalog item to be imported
   * @returns An observable containing the response from the server.
   */
  public createComponentsImport(file: File, importType: string) {
    const formData = new FormData();
    let endpoint = "";
    switch (importType) {
      case GATE_CATALOG_ITEM_TYPES.MANUFACTURER:
        formData.append("manufacturerFile", file);
        endpoint = `${environment.baseUrl}/import/manufacturer`;
        break;
      case GATE_CATALOG_ITEM_TYPES.MAKE:
        formData.append("modelFile", file);
        endpoint = `${environment.baseUrl}/import/model`;
        break;
      case GATE_CATALOG_ITEM_TYPES.ARTICLE:
        formData.append("articleFile", file);
        endpoint = `${environment.baseUrl}/import/article`;
        break;
      default:
        break;
    }

    return this.http.post<ImportResponse>(endpoint, formData).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.error?.importFailedCsvFilename) {
          return throwError(() => error);
        }

        return throwError(() => error).pipe(this.apiErrorHandler.handle(`gate_${importType.toLowerCase()}`));
      })
    );
  }

  /**
   * Retrieves a failed csv import file from the server.
   * @param filename The filename of the failed csv file to be retrieved.
   * @returns An observable containing the file as a blob.
   */
  public getComponentsFailedFile(filename: string) {
    return this.http
      .get(`${environment.baseUrl}/import/failed/${filename}`, {
        observe: "response",
        responseType: "blob",
      })
      .pipe(this.apiErrorHandler.handle("download_failed_import"));
  }

  /**
   * Retrieves an example csv import file from the server for the specified gate catalog item type.
   * @param importType The type of the gate catalog item to be imported.
   * @returns An observable containing the example file as a blob.
   */
  public getComponentsExampleFile(importType: string) {
    let exampleFileType = "";
    switch (importType) {
      case GATE_CATALOG_ITEM_TYPES.MANUFACTURER:
        exampleFileType = GATE_CATALOG_ITEM_TYPES.MANUFACTURER;
        break;
      case GATE_CATALOG_ITEM_TYPES.MAKE:
        exampleFileType = "MODEL";
        break;
      case GATE_CATALOG_ITEM_TYPES.ARTICLE:
        exampleFileType = GATE_CATALOG_ITEM_TYPES.ARTICLE;
        break;
      default:
        break;
    }
    return this.http
      .get(`${environment.baseUrl}/import/example?exportExampleType=${exampleFileType}`, {
        observe: "response",
        responseType: "blob",
      })
      .pipe(this.apiErrorHandler.handle("template_file"));
  }

  /**
   * DEVICES IMPORT FUNCTIONS
   */

  /**
   * Retrieves a device import example file from the server.
   * @returns An observable containing the file as a blob.
   */
  public getDeviceExampleFile() {
    return this.http
      .get(`${environment.baseUrl}/device-import/example`, {
        observe: "response",
        responseType: "blob",
      })
      .pipe(this.apiErrorHandler.handle("template_file"));
  }

  /**
   * Retrieves a failed csv import file from the server.
   * @param filename The filename of the failed csv file to be retrieved.
   * @returns An observable containing the file as a blob.
   */
  public getDevicesFailedFile(filename: string) {
    return this.http
      .get(`${environment.baseUrl}/device-import/failed/${filename}`, {
        observe: "response",
        responseType: "blob",
      })
      .pipe(this.apiErrorHandler.handle("download_failed_import"));
  }

  /**
   * Imports devices.
   * @param importFile The file to be imported containing the device data.
   * @returns An observable containing the response from the server.
   */
  public createDeviceImport(importFile: File) {
    const formData = new FormData();
    formData.append("deviceFile", importFile);
    return this.http
      .post<ImportResponse>(`${environment.baseUrl}/device-import`, formData)
      .pipe(this.apiErrorHandler.handle("device_import"));
  }
}
